/* eslint-disable react/no-children-prop */
import { memo, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import SmartDPProductList from "app/pages/SmartDP/Listing/SmartDPProductList/SmartDPProductList";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import SmartDPListingListLoader from "app/pages/SmartDP/Listing/SmartDPListingListLoader";
import SelectedFlightRecapContainer from "app/pages/Booking/Quotation/Flight/SelectedFlightRecap/SelectedFlightRecapContainer";
import "./SmartDPListingList.scss";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { useFacets, useFilterParam } from "app/features/product/productFilters";
import {
	FLAGSHIP_EVENT_ON_SORT_SELECTION,
	getSortLabelKey,
	getSortLabel,
	SortTrigger,
	useSortContext,
	useSortedProducts,
} from "app/features/product/productSort";
import { Button } from "app/components/ui/Button/Button";
import { ChevronDown } from "app/icons/navigation";
import { FormattedMessage, useIntl } from "react-intl";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { useSelector } from "react-redux";
import { shouldShowPaymentBanner } from "app/reducers/partnerSelector";
import BookingLoadingPrice from "app/pages/Booking/BookingLoadingPrice";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import {
	QUOTATION_CODE_STATUS,
	QUOTATION_STATUS_VALIDE,
	SDP_PRODUCTS_FILTERS_KEY,
} from "app/constants";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { HotelFilledIcon } from "app/icons/travel";
import SdpPartialSuccessAlertMessage from "app/pages/SmartDP/Listing/SdpPartialSuccessAlertMessage/SdpPartialSuccessAlertMessage";
import SdpCabinFallbackAlertMessage from "app/pages/SmartDP/Listing/SdpCabinFallbackAlertMessage/SdpCabinFallbackAlertMessage";

const SDP_FILTERS = Object.values(SDP_PRODUCTS_FILTERS_KEY);

const SmartDPListingList = ({
	fetchSDPProductsStatus = QUOTATION_CODE_STATUS.LOADING,
	onAccommodationClick = {},
	spotlightAccommodation = {},
	unavailableAccommodation = {},
	onSelectedflightRecapClick = () => {},
	isStopOver,
	quotationItems = [],
	destinationLabel,
}) => {
	const { track } = useAnalytics();
	const isPaymentDescriptionBlockExist = useSelector(shouldShowPaymentBanner);
	const { sendHits } = useFlagship();
	const { formatMessage } = useIntl();
	const isLoading = !QUOTATION_STATUS_VALIDE.includes(fetchSDPProductsStatus);

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const { filteredProducts: filteredAccommodations } = useFacets();
	const { filters, selectedFiltersCount } = useFilterParam(SDP_FILTERS);

	const { sortedProducts: sortedAccommodations, sortBy } =
		useSortedProducts(filteredAccommodations);

	const { options } = useSortContext();

	const handleSortOpen = isSticky => {
		track("user_click_sort-button", {
			sortType: sortBy,
			viewType: "list",
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleCurrentSortOpen = () => {
		track("user_click_current-sort-button", {
			sortType: sortBy,
			viewType: "list",
		});
	};

	const handleMobileFilterOpen = isSticky => {
		track("listingPage_filters_mobile_open", {
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleFilterOpen = (filterId, isSticky) => {
		track(`listingPage_filter_by_${filterId}_open`, {
			viewType: "list",
			...(!isMobile &&
				typeof isSticky === "boolean" && { filterPosition: isSticky ? "sticky" : "top" }),
		});
	};

	const handleApplyFilters = (selectedFilters, resultsCount) => {
		track("sdp-filters-view-offers-button-clicked", {
			productCount: resultsCount,
			filters: selectedFilters,
		});
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });

		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_ON_SORT_SELECTION[sortById],
		});
	};

	const sortByLabel =
		getSortLabel(sortBy, options) || formatMessage({ id: getSortLabelKey(sortBy, options) });

	useEffect(() => {
		if (selectedFiltersCount === 0) {
			return;
		}

		track("product_list_filtered", {
			productCount: sortedAccommodations.length,
			filters,
		});
	}, [filters]);

	return (
		<div className="sdp-listing-list">
			{!isMobile && (
				<>
					{isLoading && <BookingLoadingPrice />}

					{isStopOver && (
						<div className="sdp-listing-list__selected-flight-recap">
							<SelectedFlightRecapContainer
								isLoading={isLoading}
								onClick={onSelectedflightRecapClick}
							/>
						</div>
					)}

					{!isLoading && isPaymentDescriptionBlockExist && (
						<div className="sdp-listing-list__payment-description">
							<PaymentDescriptionContainer />
						</div>
					)}

					{isLoading ? (
						<div className="sdp-listing-list__title sdp-listing-list__title--loader">
							<LoaderBar height={12} width={100} />
						</div>
					) : (
						<Typography
							variant={
								isStopOver ? TYPOGRAPHY_VARIANTS.LARGE : TYPOGRAPHY_VARIANTS.REGULAR
							}
							variantMD={
								isStopOver ? TYPOGRAPHY_VARIANTS.XL : TYPOGRAPHY_VARIANTS.REGULAR
							}
							isBold={isStopOver}
							className="sdp-listing-list__title"
							component="h2"
							data-testid="sdp-product-list-title"
						>
							{isStopOver ? (
								<>
									<HotelFilledIcon
										width={16}
										height={16}
										aria-hidden={true}
										focusable={false}
									/>
									<FormattedMessage
										id="sdp.listing.stopover.title"
										values={{
											city: destinationLabel,
											count: sortedAccommodations.length,
										}}
									/>
								</>
							) : (
								<FormattedMessage
									id="listing.title"
									values={{
										count: sortedAccommodations.length,
									}}
								/>
							)}
						</Typography>
					)}
				</>
			)}

			<ActionBar
				isSortAvailable={true}
				showMiniSearch={true}
				onSortOpen={handleSortOpen}
				onFilterOpen={handleFilterOpen}
				showMapButton={true}
				isLoading={isLoading}
				variant="borderless"
				onApplyFilters={handleApplyFilters}
				onSortChange={handleSortChange}
				onFilterTriggerMobileOpen={handleMobileFilterOpen}
			/>

			{isMobile && (
				<>
					{isLoading && <BookingLoadingPrice />}

					{isStopOver && (
						<>
							<div className="sdp-listing-list__selected-flight-recap">
								<SelectedFlightRecapContainer
									isLoading={isLoading}
									onClick={onSelectedflightRecapClick}
								/>
							</div>

							{isLoading ? (
								<div className="sdp-listing-list__mobile-destination sdp-listing-list__mobile-destination--loader">
									<LoaderBar width={300} height={12} />
								</div>
							) : (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									component="h2"
									isBold
									className="sdp-listing-list__mobile-destination"
								>
									<HotelFilledIcon
										aria-hidden="true"
										focusable="false"
										width={16}
										height={16}
									/>

									<FormattedMessage
										id="page.stopover.listing.title"
										values={{
											destination: destinationLabel,
										}}
									/>
								</Typography>
							)}
						</>
					)}
				</>
			)}

			{isMobile && !isLoading && isPaymentDescriptionBlockExist && (
				<div className="sdp-listing-list__payment-description">
					<PaymentDescriptionContainer />
				</div>
			)}

			{isMobile && (
				<>
					{isLoading ? (
						<div
							className="sdp-listing-list__mobile-results-title sdp-listing-list__mobile-results-title--loader"
							aria-hidden="true"
						>
							<LoaderBar width={92} height={12} />
							<LoaderBar width={92} height={12} />
						</div>
					) : (
						<section className="sdp-listing-list__mobile-results-title">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								component="p"
								className="sdp-listing-list__title"
							>
								<FormattedMessage
									id="listing.title"
									values={{ count: sortedAccommodations.length }}
								/>
							</Typography>

							<SortTrigger asChild onClick={handleCurrentSortOpen}>
								<Button
									variant="borderless"
									type="button"
									endIcon={<ChevronDown />}
								>
									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										component="span"
									>
										{sortByLabel}
									</Typography>
								</Button>
							</SortTrigger>
						</section>
					)}
				</>
			)}

			{fetchSDPProductsStatus === QUOTATION_CODE_STATUS.PARTIAL_SUCCESS && (
				<SdpPartialSuccessAlertMessage accommodation={unavailableAccommodation} />
			)}

			<SdpCabinFallbackAlertMessage />

			{isLoading && <SmartDPListingListLoader isMobile={isMobile} />}

			{!isLoading && (
				<SmartDPProductList
					spotlightAccommodationId={spotlightAccommodation?.id}
					accommodationsList={sortedAccommodations}
					onAccommodationClick={onAccommodationClick}
					quotationItems={quotationItems}
					isStopOver={isStopOver}
				/>
			)}

			<ScrollToTopButton />
		</div>
	);
};

SmartDPListingList.propTypes = {
	fetchSDPProductsStatus: PropTypes.string,
	onSelectedflightRecapClick: PropTypes.func,
	onAccommodationClick: PropTypes.func,
	spotlightAccommodation: PropTypes.object,
	unavailableAccommodation: PropTypes.object,
	isStopOver: PropTypes.bool,
	quotationItems: PropTypes.array,
	destinationLabel: PropTypes.string,
};

export default memo(SmartDPListingList);
